"use client";

import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import Image from "next/image";
import { useLayoutEffect, useRef } from "react";

interface MarqueeImage {
  readonly src: string;
}

export function SgfImageMarquee({
  leftImages,
  rightImages,
}: {
  leftImages: readonly MarqueeImage[];
  rightImages: readonly MarqueeImage[];
}) {
  const yLeft = useMotionValue(0);
  const yRight = useMotionValue(0);
  const leftSetRef = useRef<HTMLDivElement>(null);
  // Cache offsetHeight so the rAF loop never reads from the DOM
  const cachedHeightRef = useRef(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (leftSetRef.current) {
        cachedHeightRef.current = leftSetRef.current.offsetHeight;
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (leftSetRef.current) {
      ro.observe(leftSetRef.current);
    }
    return () => ro.disconnect();
  }, []);

  useAnimationFrame((_, delta) => {
    const setHeight = cachedHeightRef.current;
    if (!setHeight) {
      return;
    }
    // 25s per set — matches the horizontal logo marquee pace
    const moved = (delta * (setHeight / 25)) / 1000;

    let nextLeft = yLeft.get() - moved;
    if (nextLeft <= -setHeight) {
      nextLeft += setHeight;
    }
    yLeft.set(nextLeft);

    let nextRight = yRight.get() - moved;
    if (nextRight <= -setHeight) {
      nextRight += setHeight;
    }
    yRight.set(nextRight);
  });

  return (
    <div
      aria-hidden="true"
      className="absolute inset-y-0 right-0 hidden h-full w-[42%] gap-3 overflow-hidden pr-4 sm:pr-6 md:flex md:w-[38%] lg:w-[42%]"
    >
      {/* Left column — offset by -18rem for brick-wall stagger */}
      <div
        className="flex flex-1 flex-col overflow-hidden"
        style={{ marginTop: "-18rem" }}
      >
        <motion.div className="flex flex-col" style={{ y: yLeft }}>
          {/* Primary set — measured to determine loop stride */}
          <div className="flex flex-col" ref={leftSetRef}>
            {leftImages.map((img) => (
              <div
                className="relative mb-3 aspect-[3/4] shrink-0 overflow-hidden rounded-2xl bg-muted"
                key={img.src}
              >
                <Image
                  alt=""
                  className="object-cover"
                  fill
                  sizes="(max-width: 1280px) 25vw, 320px"
                  src={img.src}
                />
              </div>
            ))}
          </div>
          {/* Duplicate set for seamless loop */}
          <div aria-hidden="true" className="flex flex-col">
            {leftImages.map((img, i) => (
              <div
                className="relative mb-3 aspect-[3/4] shrink-0 overflow-hidden rounded-2xl bg-muted"
                // biome-ignore lint/suspicious/noArrayIndexKey: static duplicate list, src is not unique
                key={i}
              >
                <Image
                  alt=""
                  className="object-cover"
                  fill
                  sizes="(max-width: 1280px) 25vw, 320px"
                  src={img.src}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right column — offset by -6rem for brick-wall stagger */}
      <div
        className="flex flex-1 flex-col overflow-hidden"
        style={{ marginTop: "-6rem" }}
      >
        <motion.div className="flex flex-col" style={{ y: yRight }}>
          <div className="flex flex-col">
            {rightImages.map((img) => (
              <div
                className="relative mb-3 aspect-[3/4] shrink-0 overflow-hidden rounded-2xl bg-muted"
                key={img.src}
              >
                <Image
                  alt=""
                  className="object-cover"
                  fill
                  sizes="(max-width: 1280px) 25vw, 320px"
                  src={img.src}
                />
              </div>
            ))}
          </div>
          <div aria-hidden="true" className="flex flex-col">
            {rightImages.map((img, i) => (
              <div
                className="relative mb-3 aspect-[3/4] shrink-0 overflow-hidden rounded-2xl bg-muted"
                // biome-ignore lint/suspicious/noArrayIndexKey: static duplicate list, src is not unique
                key={i}
              >
                <Image
                  alt=""
                  className="object-cover"
                  fill
                  sizes="(max-width: 1280px) 25vw, 320px"
                  src={img.src}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
